import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, UploadCloud, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

export const DEPARTMENTS = [
  "College of Engineering",
  "College of Computer Science",
  "College of Fine Arts & Design",
  "College of Medicine & Pharmacy",
  "School of Business",
];

export const CATEGORIES = ["AI & Robotics", "Bio & Health", "Digital Arts & Design", "Smart Mobility", "Future Architecture"];

const OTHER = "__other__";
const EMPTY = { title: "", tagline: "", department: "", other_department: "", category: "", other_category: "", description: "", team_members: "", team_emails: "", tech_stack: "", demo_url: "", github_url: "", video_url: "", award: "", image_url: "" };

export default function SubmissionModal({ open, onClose, onSubmitted }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState(true);
  const fileRef = useRef(null);
  const { t } = useLang();
  const { user } = useAuth();

  useEffect(() => {
    if (open) api.get("/settings").then((r) => setAllowed(r.data.submissions !== false)).catch(() => {});
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const deptValue = form.department === OTHER ? form.other_department.trim() : form.department;
  const catValue = form.category === OTHER ? form.other_category.trim() : form.category;
  const step1Valid = form.title.trim() && deptValue && catValue;
  const step2Valid = form.description.trim().length >= 30;

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      const payload = { ...form, department: deptValue, category: catValue };
      delete payload.other_department;
      delete payload.other_category;
      Object.entries(payload).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("image", file);
      const res = await api.post("/projects", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t.sub_success, { description: t.sub_success_desc });
      setForm(EMPTY);
      setFile(null);
      setStep(0);
      onSubmitted?.(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#2563EB] focus:bg-white";
  const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/50 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={onClose}
          data-testid="submission-modal"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-lenis-prevent className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-slate-200 bg-white p-7 shadow-2xl sm:rounded-2xl sm:p-9"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2563EB]">{t.sub_step} {step + 1} {t.sub_of} 2</p>
                <h2 className="mt-2 font-display text-2xl font-bold text-slate-900">{t.sub_title}</h2>
                <p className="mt-1 text-sm text-slate-500">{t.sub_subtitle}</p>
                {user && (
                  <p className="mt-2 inline-flex items-center gap-2 rounded-full border border-[#2563EB]/25 bg-[#2563EB]/5 px-3 py-1 font-mono text-[10px] text-[#1E3A8A]" data-testid="submission-user-note">
                    {t.auth_submitting_as} {user.name}{user.student_id ? ` · ${user.student_id}` : ""}
                  </p>
                )}
              </div>
              <button onClick={onClose} data-testid="submission-close" className="rounded-full border border-slate-200 p-2 text-slate-400 transition-colors hover:text-slate-900">
                <X size={16} />
              </button>
            </div>

            <div className="mt-6 h-1 overflow-hidden rounded-full bg-slate-100">
              <motion.div className="h-full bg-[#2563EB]" animate={{ width: step === 0 ? "50%" : "100%" }} transition={{ duration: 0.5 }} />
            </div>

            {!allowed ? (
              <div className="mt-10 flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center" data-testid="submission-closed">
                <Lock size={22} className="text-slate-300" />
                <p className="font-display text-lg font-semibold text-slate-800">{t.sub_closed}</p>
                <p className="max-w-sm text-sm text-slate-500">{t.sub_closed_body}</p>
              </div>
            ) : step === 0 ? (
              <div className="mt-7 space-y-5">
                <div>
                  <label className={labelCls}>{t.sub_f_title} *</label>
                  <input data-testid="submission-title-input" className={inputCls} placeholder={t.sub_f_title_ph} value={form.title} onChange={set("title")} />
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_tagline}</label>
                  <input data-testid="submission-tagline-input" className={inputCls} placeholder={t.sub_f_tagline_ph} value={form.tagline} onChange={set("tagline")} />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>{t.sub_f_dept} *</label>
                    <select data-testid="submission-department-select" className={inputCls} value={form.department} onChange={set("department")}>
                      <option value="">{t.sub_f_dept_ph}</option>
                      {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                      <option value={OTHER}>{t.sub_f_other}</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{t.sub_f_cat} *</label>
                    <select data-testid="submission-category-select" className={inputCls} value={form.category} onChange={set("category")}>
                      <option value="">{t.sub_f_cat_ph}</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      <option value={OTHER}>{t.sub_f_other}</option>
                    </select>
                  </div>
                </div>
                {form.category === OTHER && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.3 }}>
                    <label className={labelCls}>{t.sub_f_other} *</label>
                    <input data-testid="submission-other-category-input" className={inputCls} placeholder={t.sub_f_other_cat_ph} value={form.other_category} onChange={set("other_category")} />
                  </motion.div>
                )}
                {form.department === OTHER && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} transition={{ duration: 0.3 }}>
                    <label className={labelCls}>{t.sub_f_other} *</label>
                    <input data-testid="submission-other-department-input" className={inputCls} placeholder={t.sub_f_other_ph} value={form.other_department} onChange={set("other_department")} />
                  </motion.div>
                )}
                <button
                  onClick={() => setStep(1)}
                  disabled={!step1Valid}
                  data-testid="submission-next-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1E3A8A] py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-white transition-all duration-300 hover:bg-[#2563EB] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t.sub_next} <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <div className="mt-7 space-y-5">
                <div>
                  <label className={labelCls}>{t.sub_f_desc} * (min 30 chars)</label>
                  <textarea data-testid="submission-description-input" rows={4} className={inputCls} placeholder={t.sub_f_desc_ph} value={form.description} onChange={set("description")} />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>{t.sub_f_team}</label>
                    <input data-testid="submission-team-input" className={inputCls} placeholder={t.sub_f_team_ph} value={form.team_members} onChange={set("team_members")} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.sub_f_emails}</label>
                    <input data-testid="submission-emails-input" className={inputCls} placeholder={t.sub_f_emails_ph} value={form.team_emails} onChange={set("team_emails")} />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>{t.sub_f_tech}</label>
                    <input data-testid="submission-tech-input" className={inputCls} placeholder={t.sub_f_tech_ph} value={form.tech_stack} onChange={set("tech_stack")} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.sub_f_video}</label>
                    <input data-testid="submission-video-input" className={inputCls} placeholder="https://youtube.com/watch?v=…" value={form.video_url} onChange={set("video_url")} />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>{t.sub_f_demo}</label>
                    <input data-testid="submission-demo-input" className={inputCls} placeholder="https://..." value={form.demo_url} onChange={set("demo_url")} />
                  </div>
                  <div>
                    <label className={labelCls}>{t.sub_f_github}</label>
                    <input data-testid="submission-github-input" className={inputCls} placeholder="https://github.com/..." value={form.github_url} onChange={set("github_url")} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_award}</label>
                  <input data-testid="submission-award-input" className={inputCls} placeholder={t.sub_f_award_ph} value={form.award} onChange={set("award")} />
                </div>
                <div>
                  <label className={labelCls}>{t.sub_f_image}</label>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="submission-image-upload"
                    className="flex w-full items-center justify-center gap-3 rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500 transition-colors hover:border-[#2563EB]/60 hover:text-[#1E3A8A]"
                  >
                    {file ? <><CheckCircle2 size={16} className="text-emerald-500" /> {file.name}</> : <><UploadCloud size={16} /> {t.sub_f_upload}</>}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  <input data-testid="submission-image-url-input" className={`${inputCls} mt-3`} placeholder={t.sub_f_imgurl_ph} value={form.image_url} onChange={set("image_url")} />
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setStep(0)} data-testid="submission-back-button" className="flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-slate-600 transition-colors hover:text-slate-900">
                    <ArrowLeft size={14} /> {t.sub_back}
                  </button>
                  <button
                    onClick={submit}
                    disabled={!step2Valid || busy}
                    data-testid="submission-submit-button"
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-600 py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-white transition-all duration-300 hover:bg-emerald-500 hover:shadow-[0_10px_28px_rgba(16,185,129,0.35)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {busy ? t.sub_submitting : t.sub_submit}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
